import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { Card, CardHeader, Box } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { useChart } from "../../../components/chart";
import { useGetMembersQuery } from "../../../features/members/membersApiSlice";
import UserRolesBadge from "../../../components/rolesbadge/UserRolesBadge";

const AGE_GROUPS = [
  { label: "Bambini", rangeLabel: "0-5", min: 0, max: 5 },
  { label: "Kinder", rangeLabel: "6-11", min: 6, max: 11 },
  { label: "Schüler", rangeLabel: "12-14", min: 12, max: 14 },
  { label: "Jugend", rangeLabel: "12-17", min: 12, max: 17 },
  { label: "Erwachsen", rangeLabel: "18-49", min: 18, max: 49 },
  { label: "Senioren", rangeLabel: "50-99+", min: 50, max: Infinity },
];

const calculateAge = (birthdate) => {
  if (!birthdate) return null;
  const date = new Date(birthdate);
  if (Number.isNaN(date.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  const dayDiff = today.getDate() - date.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age;
};

export default function ActiveMembersByAgeGroup() {
  const theme = useTheme();
  const { data: members = [], isLoading } = useGetMembersQuery();

  const chartData = useMemo(() => {
    const counts = new Map(AGE_GROUPS.map((group) => [group.label, 0]));

    (members || []).forEach((member) => {
      if ((member?.status || "").toLowerCase() !== "active") return;

      const age = calculateAge(member?.birthdate);
      if (age === null || age < 0) return;

      const matchingGroup = AGE_GROUPS.find(
        (group) => age >= group.min && age <= group.max,
      );

      if (matchingGroup) {
        counts.set(
          matchingGroup.label,
          (counts.get(matchingGroup.label) || 0) + 1,
        );
      }
    });

    return AGE_GROUPS.map((group) => ({
      ...group,
      value: counts.get(group.label) || 0,
    }));
  }, [members]);

  const categories = useMemo(
    () => chartData.map((group) => `${group.label} (${group.rangeLabel})`),
    [chartData],
  );

  const series = useMemo(
    () => [
      {
        name: "Aktive Mitglieder",
        data: chartData.map((group) => group.value),
      },
    ],
    [chartData],
  );

  const colors = useMemo(() => {
    const palette = [
      theme.palette.primary.main,
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.secondary.main,
    ];
    return chartData.map((_, idx) => palette[idx % palette.length]);
  }, [chartData, theme.palette]);

  const chartOptions = useChart({
    colors,
    labels: categories,
    plotOptions: {
      bar: {
        columnWidth: "48%",
        distributed: true,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}`,
      offsetY: -18,
      style: {
        fontSize: "12px",
        colors: [theme.palette.text.primary],
      },
    },
    xaxis: {
      categories,
      labels: {
        style: { colors: theme.palette.text.secondary },
        trim: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (value) => `${Math.floor(value)}`,
      },
      title: {
        text: "Mitglieder",
        style: { color: theme.palette.text.secondary },
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      y: {
        formatter: (val) => `${val} aktive Mitglieder`,
      },
      x: {
        formatter: (val) => val,
      },
    },
    legend: { show: false },
  });

  return (
    <Card>
      <CardHeader
        title="Aktive Mitglieder nach Altersgruppe"
        subheader={
          isLoading ? (
            "Lade Daten ..."
          ) : (
            <p style={{ margin: 0 }}>
              Verteilung aktiver Mitglieder nach Alter - Nur für{"  "}
              <UserRolesBadge status="Admin" /> sichtbar.
            </p>
          )
        }
      />

      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart
          type="bar"
          series={series}
          options={chartOptions}
          height={364}
        />
      </Box>
    </Card>
  );
}
